import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { showNotification } from "../../../../../constants";
import { uploadSalesImage } from "../../hooks/SalesPagesHook";
import {
  DEFAULT_IMAGE_SIZE,
  IMAGE_SIZE_OPTIONS,
  IMAGE_SIZE_PRESETS,
  describeOptimization,
  optimizeImageFile,
} from "../../../../../utils/imageOptimization";

/**
 * Asset upload field for the Sales CMS: uploads to Firebase Storage and stores
 * the resulting download URL. Also accepts a pasted URL.
 *
 * Props:
 * - accept: input `accept` filter (default "image/*").
 * - isImage: if true, render an <img> preview (default true).
 * - noun: label noun for the button/messages, e.g. "Image" or "File".
 */
const ImageUploadField = ({
  label,
  value,
  page,
  disabled,
  onChange,
  accept = "image/*",
  isImage = true,
  noun = "Image",
  defaultSize = DEFAULT_IMAGE_SIZE,
}) => {
  const [uploading, setUploading] = useState(false);
  const [size, setSize] = useState(
    IMAGE_SIZE_PRESETS[defaultSize] ? defaultSize : DEFAULT_IMAGE_SIZE
  );
  const [lastResult, setLastResult] = useState("");

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (isImage && !file.type.startsWith("image/")) {
      showNotification("Please select an image file");
      return;
    }
    try {
      setUploading(true);
      let toUpload = file;
      let meta = {};
      if (isImage) {
        const result = await optimizeImageFile(file, {
          maxDimension: IMAGE_SIZE_PRESETS[size].maxDimension,
        });
        toUpload = result.file;
        meta = { imageSize: size, originalName: file.name };
        const summary = describeOptimization(result);
        setLastResult(summary);
        showNotification(summary);
      }
      const url = await uploadSalesImage(toUpload, page, meta);
      onChange(url);
    } catch (error) {
      console.log("Error uploading file:", error.message);
      showNotification(`Error uploading ${noun.toLowerCase()}`);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={labelStyles}>{label}</Typography>
      {value && isImage ? (
        <Box sx={{ mb: 1 }}>
          <img src={value} alt={label} style={previewStyles} />
        </Box>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          variant="outlined"
          component="label"
          size="small"
          disabled={disabled || uploading}
          style={buttonStyles}
        >
          {uploading ? (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          ) : null}
          {value ? `Replace ${noun}` : `Upload ${noun}`}
          <input hidden type="file" accept={accept} onChange={handleFile} />
        </Button>
        {isImage ? (
          <TextField
            select
            size="small"
            label="Max size"
            value={size}
            disabled={disabled || uploading}
            onChange={(e) => setSize(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            {IMAGE_SIZE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {value && !disabled ? (
          <Button
            variant="text"
            size="small"
            style={{ textTransform: "none", color: "#B00020" }}
            onClick={() => onChange("")}
          >
            Remove
          </Button>
        ) : null}
      </Box>
      {lastResult ? (
        <Typography sx={{ ...urlStyles, color: "#2E7D32" }}>
          {lastResult}
        </Typography>
      ) : null}
      {value ? (
        <Typography sx={urlStyles} noWrap title={value}>
          {value}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ImageUploadField;

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};

const urlStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 11,
  color: "#808080",
  mt: 0.5,
  maxWidth: 500,
};

const buttonStyles = {
  textTransform: "none",
  color: "#00A7CD",
  borderColor: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};

const previewStyles = {
  maxWidth: 220,
  maxHeight: 140,
  borderRadius: 8,
  objectFit: "cover",
  border: "1px solid #E0E0E0",
};
